import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import pedidoVentaService, {
  CopiarPedidoRelacionadosResult,
} from '../../../services/pedidoVentaService';
import { getApiErrorMessage } from '../../../utils/apiError';

interface CopiarPedidoRelacionadoDialogProps {
  open: boolean;
  pedidoIds: number[];
  onClose: () => void;
  onCopied: () => void;
  title?: string;
  description?: string;
  confirmLabel?: string;
}

const CopiarPedidoRelacionadoDialog: React.FC<CopiarPedidoRelacionadoDialogProps> = ({
  open,
  pedidoIds,
  onClose,
  onCopied,
  title = 'Enviar',
  description = 'Se envía la Cant. conv. al destino configurado en la sucursal de venta (tenant, compañía y sucursal). El pedido origen conserva la Cant. origen.',
  confirmLabel = 'Enviar',
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultado, setResultado] = useState<CopiarPedidoRelacionadosResult | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setResultado(null);
    setSaving(false);
  }, [open, pedidoIds.join(',')]);

  const handleCopiar = async () => {
    const ids = pedidoIds.filter((id) => Number.isFinite(id) && id > 0);
    if (ids.length === 0) return;
    try {
      setSaving(true);
      setError(null);
      const acumulado: CopiarPedidoRelacionadosResult = {
        nroFormularioOrigen: ids[0],
        pedidoOrigen: null,
        destinos: [],
      };
      const errores: string[] = [];
      for (const id of ids) {
        try {
          const data = await pedidoVentaService.copiarRelacionados(id);
          acumulado.destinos.push(...data.destinos);
          if (data.pedidoOrigen) acumulado.pedidoOrigen = data.pedidoOrigen;
        } catch (err: unknown) {
          errores.push(`Pedido ${id}: ${getApiErrorMessage(err) || 'No se pudo enviar.'}`);
        }
      }
      if (acumulado.destinos.length === 0 && errores.length > 0) {
        setError(errores.join(' '));
        return;
      }
      setResultado(acumulado);
      const ok = acumulado.destinos.filter((d) => d.ok);
      const fallidos = acumulado.destinos.filter((d) => !d.ok);
      if (ok.length > 0) {
        enqueueSnackbar(
          ok.length === 1
            ? `Pedido enviado a ${ok[0].tenantCode || 'la empresa relacionada'}.`
            : `Pedido enviado a ${ok.length} destinos.`,
          { variant: fallidos.length > 0 || errores.length > 0 ? 'warning' : 'success' },
        );
      } else {
        enqueueSnackbar('No se pudo enviar el pedido.', { variant: 'error' });
      }
      if (errores.length > 0) setError(errores.join(' '));
      onCopied();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err) || 'No se pudo enviar el pedido.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent dividers>
        {resultado ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              El pedido origen quedó con la Cant. origen y sin Cant. conv. Para ver un pedido
              destino hay que cambiar a esa empresa.
            </Typography>
            {resultado.destinos.map((destino) => (
              <Alert
                key={`${destino.tenantId}-${destino.nroFormulario ?? 'x'}`}
                severity={destino.ok ? 'success' : 'error'}
              >
                {destino.ok
                  ? `${destino.tenantCode || `Empresa ${destino.tenantId}`}: pedido ${destino.nroFormulario ?? '—'}`
                  : `${destino.tenantCode || `Empresa ${destino.tenantId}`}: ${destino.error || 'No se pudo crear el pedido.'}`}
              </Alert>
            ))}
            {resultado.destinos.length === 0 && (
              <Alert severity="warning">El envío no devolvió destinos.</Alert>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
          Cerrar
        </Button>
        {!resultado && (
          <Button
            variant="contained"
            onClick={() => void handleCopiar()}
            disabled={saving || pedidoIds.length === 0}
            sx={{ textTransform: 'none' }}
          >
            {saving ? 'Enviando…' : confirmLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CopiarPedidoRelacionadoDialog;
